export type LogoutFn = () => void;
export type AccessTokenFn = () => Promise<string>;

const FORCED_LOGOUT_KEY = "bi_dashboards_forced_logout_at";
const FORCED_LOGOUT_COOLDOWN = 60 * 1000;

class AuthService {
	private accessTokenFn: AccessTokenFn = null;
	private logoutFn: LogoutFn = null;
	private isLoggingOut: boolean = false;

	public setAccessTokenProvider(fn: AccessTokenFn) {
		this.accessTokenFn = fn;
	}

	// Auth0 hands back the cached token and only calls out to renew it when it
	// is about to expire, so asking per request keeps the token always valid.
	public async getAccessToken(): Promise<string> {
		if (!this.accessTokenFn) {
			return null;
		}

		try {
			return await this.accessTokenFn();
		} catch (err) {
			// No token means the Auth0 session is gone.
			this.forceLogout();
			return null;
		}
	}

	public setLogout(fn: LogoutFn) {
		this.logoutFn = fn;
	}

	public logout() {
		if (this.isLoggingOut) {
			return;
		}

		this.isLoggingOut = true;

		if (this.logoutFn) {
			this.logoutFn();
		}
	}

	// Signing out goes through Auth0, which can sign the user straight back in
	// when the identity provider session is still alive. Allowing that once per
	// minute keeps a rejected token from turning into a redirect loop.
	public forceLogout() {
		if (Date.now() - this.getLastForcedLogout() < FORCED_LOGOUT_COOLDOWN) {
			return;
		}

		this.setLastForcedLogout(Date.now());
		this.logout();
	}

	private getLastForcedLogout(): number {
		try {
			return Number(sessionStorage.getItem(FORCED_LOGOUT_KEY)) || 0;
		} catch (e) {
			return 0;
		}
	}

	private setLastForcedLogout(time: number) {
		try {
			sessionStorage.setItem(FORCED_LOGOUT_KEY, `${time}`);
		} catch (e) {
			// Storage can be unavailable in private mode; the in-memory guard still applies.
		}
	}
}

export default new AuthService();
